import React from 'react'
import { Box, Button, Center, Img, Text, VStack } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import * as Sentry from '@sentry/react'
import { text } from '../../themes/styles'
import LogoIcon from '../../assets/wrk_logo.png'

const MotionBox = motion.create(Box)

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Auto-reload on stale chunk errors after deployment
    if (
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Importing a module script failed')
    ) {
      const reloadKey = 'chunk_reload'
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, '1')
        window.location.reload()
        return
      }
      sessionStorage.removeItem(reloadKey)
    }
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <Center bg="background.wrapper" w="100vw" minH="100vh" pt={10} pb={0}>
          <VStack spacing={0}>
            <MotionBox
              mb="32px"
              display="flex"
              justifyContent="center"
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            >
              <Img src={LogoIcon} alt="Logo" width="110px" height="55px" />
            </MotionBox>
            <MotionBox
              borderRadius="16px"
              p="40px"
              bg="solid.0"
              maxWidth="440px"
              width="440px"
              boxShadow="0px 4px 32px rgba(93, 94, 203, 0.16)"
              textAlign="center"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <VStack spacing={4}>
                <Text {...text.sectionTitle}>Something went wrong</Text>
                <Text {...text.body}>
                  An unexpected error occurred. Please try again or contact support if the problem persists.
                </Text>
                <Button variant="mono" onClick={this.handleReset} w="100%" mt={2}>
                  Try again
                </Button>
              </VStack>
            </MotionBox>
          </VStack>
        </Center>
      )
    }

    return this.props.children
  }
}
